import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  VerifiedUser as VerifiedUserIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
  CloudUpload as CloudUploadIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AccountTree as AccountTreeIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
  AlternateEmail as MentionIcon,
  Link as LinkIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import StatsCard from '../../components/admin/StatsCard';
import { adminService } from '../../services/adminService';
import { auditLogService } from '../../services/auditLogService';
import { AdminStats } from '../../types/admin';
import { AuditLog } from '../../types/admin';
import { useUser } from '../../contexts/AuthContext';

/**
 * AdminDashboard Component
 * Main admin panel dashboard showing overview statistics and quick actions
 *
 * Features:
 * - Overview statistics (users, content, moderation)
 * - Quick action buttons
 * - Recent activity feed
 * - System health indicators
 */
const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();

  // State
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load dashboard data on mount
   */
  useEffect(() => {
    loadDashboardData();
  }, []);

  /**
   * Load all dashboard data
   */
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load stats and recent activity in parallel
      const [statsData, activityData] = await Promise.all([
        adminService.getAdminStats(),
        auditLogService.getRecentLogs(10),
      ]);

      setStats(statsData);
      setRecentActivity(activityData);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get action icon for audit log entry
   */
  const getActionIcon = (action: string): React.ReactNode => {
    switch (action) {
      case 'user_approved':
        return <VerifiedUserIcon fontSize="small" color="success" />;
      case 'user_rejected':
        return <WarningIcon fontSize="small" color="error" />;
      case 'role_changed':
        return <PeopleIcon fontSize="small" color="info" />;
      case 'user_deleted':
        return <WarningIcon fontSize="small" color="error" />;
      case 'settings_updated':
        return <SettingsIcon fontSize="small" color="primary" />;
      default:
        return <HistoryIcon fontSize="small" color="action" />;
    }
  };

  /**
   * Get human-readable action label
   */
  const getActionLabel = (log: AuditLog): string => {
    switch (log.action) {
      case 'user_approved':
        return `Approved ${log.targetUserEmail}`;
      case 'user_rejected':
        return `Rejected ${log.targetUserEmail}`;
      case 'role_changed':
        return `Changed role for ${log.targetUserEmail} to ${log.details.newRole}`;
      case 'user_deleted':
        return `Deleted user ${log.targetUserEmail}`;
      case 'user_suspended':
        return `Suspended ${log.targetUserEmail}`;
      case 'user_unsuspended':
        return `Unsuspended ${log.targetUserEmail}`;
      case 'settings_updated':
        return `Updated system settings`;
      case 'bulk_action':
        return `Performed bulk action: ${log.details.action}`;
      default:
        return `${log.action.replace(/_/g, ' ')}`;
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.displayName || 'Admin'}. Here's an overview of your system.
          </Typography>
        </Box>

        {/* Overview Statistics */}
        <Grid container spacing={3} mb={4}>
          {/* Total Users */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Total Users"
              value={stats?.users.total || 0}
              icon={<PeopleIcon />}
              color="primary"
              trend={{
                value: stats?.users.newThisWeek || 0,
                label: 'new this week',
                direction: (stats?.users.newThisWeek || 0) > 0 ? 'up' : 'flat',
              }}
              clickable
              navigateTo="/admin/users"
              tooltip="View all users"
            />
          </Grid>

          {/* Pending Approvals */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Pending Approvals"
              value={stats?.users.pending || 0}
              icon={<PersonAddIcon />}
              color="warning"
              clickable
              navigateTo="/admin/users/pending"
              tooltip="Review pending user registrations"
              badge={
                (stats?.users.pending || 0) > 0
                  ? { label: 'Action Required', color: 'warning' }
                  : undefined
              }
            />
          </Grid>

          {/* Link Requests */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Link Requests"
              value={stats?.linkRequests?.pending || 0}
              icon={<LinkIcon />}
              color="info"
              subtitle={`${stats?.linkRequests?.total || 0} total requests`}
              clickable
              navigateTo="/admin/link-requests"
              tooltip="Review person link requests"
              badge={
                (stats?.linkRequests?.pending || 0) > 0
                  ? { label: 'Pending Review', color: 'info' }
                  : undefined
              }
            />
          </Grid>

          {/* Flagged Content */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Flagged Content"
              value={stats?.moderation.pendingFlags || 0}
              icon={<FlagIcon />}
              color="error"
              clickable
              navigateTo="/admin/content"
              tooltip="Review flagged content"
              badge={
                (stats?.moderation.pendingFlags || 0) > 0
                  ? { label: 'Review Needed', color: 'error' }
                  : undefined
              }
            />
          </Grid>
        </Grid>

        {/* Secondary Statistics */}
        <Grid container spacing={3} mb={4}>
          {/* Active Users */}
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'success.light' }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {stats?.users.activeThisWeek || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active Users This Week
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Content Added This Week */}
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'info.light' }}>
                    <CloudUploadIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {(stats?.content.personsAddedThisWeek || 0) +
                        (stats?.content.photosUploadedThisWeek || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Content Added This Week
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Storage Usage */}
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'warning.light' }}>
                    <PhotoLibraryIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {stats?.system.storageUsedMB || 0} MB
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Storage Used of {stats?.system.storageQuotaMB || 0} MB
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Quick Actions */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Quick Actions
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => navigate('/admin/users/pending')}
                    size="large"
                  >
                    Approve Users
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate('/admin/users')}
                    size="large"
                  >
                    Manage Users
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<FlagIcon />}
                    onClick={() => navigate('/admin/content')}
                    size="large"
                  >
                    Moderate Content
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => navigate('/admin/settings')}
                    size="large"
                  >
                    System Settings
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/admin/analytics')}
                    size="large"
                  >
                    View Analytics
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate('/admin/audit-log')}
                    size="large"
                  >
                    Audit Log
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => navigate('/admin/profile-completeness')}
                    size="large"
                  >
                    Profile Completeness
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<NotificationsIcon />}
                    onClick={() => navigate('/admin/notifications')}
                    size="large"
                  >
                    Notifications
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LinkIcon />}
                    onClick={() => navigate('/admin/link-requests')}
                    size="large"
                  >
                    Link Requests
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MentionIcon />}
                    onClick={() => navigate('/admin/mentions/analytics')}
                    size="large"
                  >
                    Mention Analytics
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => navigate('/admin/mentions/settings')}
                    size="large"
                  >
                    Mention Settings
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    onClick={() => navigate('/admin/database')}
                    size="large"
                  >
                    Database Tasks
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Recent Activity
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/admin/audit-log')}
                >
                  View All
                </Button>
              </Box>

              {recentActivity.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={4}
                >
                  <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No recent activity
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {recentActivity.map((log, index) => (
                    <React.Fragment key={log.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {getActionIcon(log.action)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap>
                              {getActionLabel(log)}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {log.timestamp
                                ? format(log.timestamp, 'MMM d, h:mm a')
                                : 'Unknown time'}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* User Role Distribution */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                User Distribution by Role
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Chip
                      label={`${stats?.users.byRole.admin || 0} Admins`}
                      color="error"
                      sx={{ mb: 1, minWidth: 120 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Chip
                      label={`${stats?.users.byRole.editor || 0} Editors`}
                      color="warning"
                      sx={{ mb: 1, minWidth: 120 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Chip
                      label={`${stats?.users.byRole.contributor || 0} Contributors`}
                      color="info"
                      sx={{ mb: 1, minWidth: 120 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Chip
                      label={`${stats?.users.byRole.viewer || 0} Viewers`}
                      color="default"
                      sx={{ mb: 1, minWidth: 120 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
